import { useEffect } from "react";

type SeoOptions = {
  title?: string;
  description?: string;
  canonical?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: string;
  keywords?: string;
  /** Map of hreflang code → absolute URL. Pass { sv, en, "x-default" } for proper alternates. */
  alternates?: Record<string, string>;
};

function setMeta(selector: string, attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const ALT_CLASS = "page-hreflang";

function setAlternates(alts?: Record<string, string>) {
  // Remove previous alternates managed by us.
  document.head.querySelectorAll(`link[rel="alternate"].${ALT_CLASS}`).forEach((n) => n.remove());
  if (!alts) return;
  for (const [hreflang, href] of Object.entries(alts)) {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
    el.classList.add(ALT_CLASS);
    document.head.appendChild(el);
  }
}

const JSONLD_ID = "page-jsonld";

export function useSEO({ title, description, canonical, jsonLd, noindex, ogImage, ogImageAlt, ogType, keywords, alternates }: SeoOptions) {
  useEffect(() => {
    if (title) {
      document.title = title;
      setMeta('meta[property="og:title"]', "property", "og:title", title);
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }
    if (description) {
      setMeta('meta[name="description"]', "name", "description", description);
      setMeta('meta[property="og:description"]', "property", "og:description", description);
      setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    const url = canonical || (typeof window !== "undefined" ? window.location.href : undefined);
    if (url) {
      setLink("canonical", url);
      setMeta('meta[property="og:url"]', "property", "og:url", url);
    }
    setMeta('meta[name="robots"]', "name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    if (ogImage) {
      setMeta('meta[property="og:image"]', "property", "og:image", ogImage);
      setMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);
    }
    if (ogImageAlt) {
      setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", ogImageAlt);
    }
    if (ogType) {
      setMeta('meta[property="og:type"]', "property", "og:type", ogType);
    }
    if (keywords) {
      setMeta('meta[name="keywords"]', "name", "keywords", keywords);
    }
    setAlternates(alternates);

    let script = document.getElementById(JSONLD_ID) as HTMLScriptElement | null;
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = JSONLD_ID;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, canonical, JSON.stringify(jsonLd), noindex, ogImage, ogImageAlt, ogType, keywords, JSON.stringify(alternates)]);
}
